import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Volume2, Check, BarChart3, Star, LogOut, Mic } from 'lucide-react';
import { TABLES, TABLE_THEMES } from '../lib/tabuada';
import { VOICES, speak, stopSpeaking, unlockSpeech } from '../lib/speech';
import { unlockAudio, playTap } from '../lib/sound';

const TAP_SCALE = { scale: 0.94 };

function VoiceAvatar({ voice, selected, onPick, onPreview }) {
  const isGirl = voice.gender === 'girl';
  const skin = isGirl ? '#FDE68A' : '#FCD34D';
  const hair = isGirl ? '#EC4899' : '#6366F1';
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        data-testid={`voice-${voice.id}`}
        onClick={() => onPick(voice.id)}
        className={`btn-3d relative h-16 w-16 rounded-full p-0 sm:h-20 sm:w-20 ${
          selected ? 'ring-4 ring-emerald-400' : ''
        }`}
        style={{ background: '#fff', borderColor: selected ? '#10B981' : '#e2e8f0' }}
      >
        <svg viewBox="0 0 64 64" className="h-full w-full">
          <circle cx="32" cy="34" r="20" fill={skin} />
          <path d={isGirl ? 'M12 30 q20 -26 40 0 q-4 -18 -20 -18 q-16 0 -20 18Z' : 'M14 28 q18 -22 36 0 q-2 -16 -18 -16 q-16 0 -18 16Z'} fill={hair} />
          <circle cx="25" cy="34" r="3" fill="#334155" />
          <circle cx="39" cy="34" r="3" fill="#334155" />
          <path d="M26 42 q6 6 12 0" stroke="#334155" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <circle cx="22" cy="40" r="3" fill="#FB7185" opacity="0.5" />
          <circle cx="42" cy="40" r="3" fill="#FB7185" opacity="0.5" />
        </svg>
        {selected && (
          <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
            <Check className="h-4 w-4" />
          </span>
        )}
      </button>
      <button
        data-testid={`voice-preview-${voice.id}`}
        onClick={() => onPreview(voice.id)}
        className="flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-bold text-purple-700 shadow-sm"
      >
        <Volume2 className="h-3 w-3" /> {voice.label}
      </button>
    </div>
  );
}

export default function TableSelectScreen({
  name,
  progress,
  selectedTable,
  selectedVoice,
  volume,
  onSelectTable,
  onSelectVoice,
  onStartLearn,
  onOpenProgress,
  onLogout,
  onRecordVoice,
}) {
  const [table, setTable] = useState(selectedTable || null);

  const pickTable = (n) => {
    unlockAudio();
    playTap();
    setTable(n);
    onSelectTable(n);
  };

  const previewVoice = (id) => {
    unlockAudio();
    stopSpeaking();
    if (id === 'mine') {
      speak('Essa é a sua própria voz gravada!', { voiceId: 'mine', volume });
      return;
    }
    const v = VOICES.find((x) => x.id === id);
    speak(`Oi! Eu sou ${v.label}. Vamos cantar a tabuada juntos!`, { voiceId: id, volume });
  };

  const pickVoice = (id) => {
    unlockAudio();
    playTap();
    onSelectVoice(id);
    previewVoice(id);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 pb-40 sm:py-8">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="font-fred text-sm font-semibold text-purple-600">
            {name ? `Oi, ${name}! 👋` : 'Bem-vindo!'}
          </p>
          <h1 className="font-fred text-3xl font-bold text-indigo-900 sm:text-4xl">
            Escolha a tabuada
          </h1>
          <p className="text-base font-bold text-purple-700">Qual tabuada você quer aprender?</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="open-progress-btn"
            onClick={onOpenProgress}
            className="btn-3d bg-white px-3 py-3 text-purple-700"
            style={{ borderColor: '#e2e8f0' }}
            title="Meu progresso"
          >
            <BarChart3 className="h-6 w-6" />
          </button>
          {onLogout && (
            <button
              data-testid="home-logout-btn"
              onClick={onLogout}
              className="btn-3d bg-white px-3 py-3 text-rose-500"
              style={{ borderColor: '#e2e8f0' }}
              title="Sair"
            >
              <LogOut className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-4">
        {TABLES.map((n) => {
          const th = TABLE_THEMES[n];
          const stars = progress?.tables?.[n]?.stars || 0;
          const active = table === n;
          return (
            <motion.button
              key={n}
              data-testid={`table-btn-${n}`}
              whileTap={TAP_SCALE}
              onClick={() => pickTable(n)}
              className={`btn-3d relative flex-col py-4 ${active ? 'ring-4 ring-white' : ''}`}
              style={{ background: th.bg, borderColor: th.border, color: th.text }}
            >
              <span className="font-fred text-4xl font-bold leading-none sm:text-5xl">{n}</span>
              <span className="text-xs font-bold opacity-90">tabuada</span>
              {stars > 0 && (
                <span className="absolute right-1 top-1 flex items-center gap-0.5 rounded-full bg-white/90 px-1.5 py-0.5 text-[10px] font-black text-amber-600">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {stars}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Voice selector */}
      <div className="card-soft mt-6 p-4 sm:p-5">
        <h2 className="mb-1 flex items-center gap-2 font-fred text-xl font-bold text-indigo-900">
          <Music className="h-5 w-5 text-pink-500" /> Escolha a voz
        </h2>
        <p className="mb-3 text-xs font-bold text-slate-500">
          Toque na carinha para escolher, no nome para ouvir 🔊
        </p>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {VOICES.map((v) => (
            <VoiceAvatar
              key={v.id}
              voice={v}
              selected={selectedVoice === v.id}
              onPick={pickVoice}
              onPreview={previewVoice}
            />
          ))}
        </div>

        {/* Voz da própria criança (gravada no aparelho) */}
        <div className="mt-4 flex flex-col items-center gap-2 rounded-2xl bg-purple-50 p-3">
          <button
            data-testid="voice-mine"
            onClick={() => pickVoice('mine')}
            className={`btn-3d flex items-center gap-2 px-4 py-2 text-base ${
              selectedVoice === 'mine' ? 'bg-emerald-400 text-white' : 'bg-white text-purple-700'
            }`}
            style={{ borderColor: selectedVoice === 'mine' ? '#10B981' : '#e2e8f0' }}
          >
            <Mic className="h-5 w-5" /> Minha voz 🎙️
            {selectedVoice === 'mine' && <Check className="h-4 w-4" />}
          </button>
          {selectedVoice === 'mine' && (
            <div className="text-center">
              <p className="text-xs font-bold text-purple-600">
                {table
                  ? `Grave você lendo a tabuada do ${table}!`
                  : 'Escolha uma tabuada acima para poder gravar.'}
              </p>
              <button
                data-testid="record-voice-btn"
                onClick={() => table && onRecordVoice(table)}
                disabled={!table}
                className="btn-3d mt-2 bg-rose-500 px-4 py-2 text-sm text-white disabled:opacity-50"
                style={{ borderColor: '#be123c' }}
              >
                <Mic className="h-4 w-4" /> Gravar minha voz
              </button>
            </div>
          )}
        </div>

        <p className="mt-3 text-center text-xs font-bold text-purple-500">
          💡 A voz ajuda você a ouvir melhor e aprender com mais diversão!
        </p>
      </div>

      {/* Sticky CTA when a table is picked */}
      <AnimatePresence>
        {table && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed inset-x-0 bottom-0 z-30 border-t-4 border-amber-200 bg-white/95 px-4 py-3 backdrop-blur-md"
          >
            <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
              <p className="font-fred text-base font-bold text-indigo-900 sm:text-lg">
                Você escolheu a tabuada do <span className="text-pink-500">{table}</span>!
              </p>
              <button
                data-testid="start-learn-btn"
                onClick={() => {
                  playTap();
                  unlockSpeech();
                  onStartLearn(table);
                }}
                className="btn-3d whitespace-nowrap bg-emerald-500 px-5 py-3 text-white sm:text-lg"
                style={{ borderColor: '#059669' }}
              >
                <Music className="h-5 w-5" /> Aprender cantando
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
